import { lazy, ComponentType, LazyExoticComponent } from 'react';

/**
 * A wrapper around React.lazy that reloads the page if the dynamic import fails.
 * This handles the "Failed to fetch dynamically imported module" error that occurs
 * when a new version is deployed and the user's browser tries to load an old chunk.
 * 
 * @param componentImport The dynamic import function, e.g., () => import('./Component')
 * @returns A React.lazy component
 */
export const lazyWithRetry = <T extends ComponentType<Record<string, unknown>>>(
    componentImport: () => Promise<{ default: T }>
): LazyExoticComponent<T> => {
    return lazy(async () => {
        const pageHasAlreadyBeenForceRefreshed = JSON.parse(
            window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
        );

        try {
            const component = await componentImport();
            window.sessionStorage.setItem('page-has-been-force-refreshed', 'false');
            return component;
        } catch (error: unknown) {
            if (!pageHasAlreadyBeenForceRefreshed) {
                // Assuming that the user is not on the latest version of the application.
                // Let's refresh the page immediately.
                window.sessionStorage.setItem('page-has-been-force-refreshed', 'true');
                window.location.reload();
            }

            // If we've already reloaded and it still fails, throw the error
            throw error;
        }
    });
};
